import React, { useCallback, useMemo, useState, useEffect } from 'react';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { ProductEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/types';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange, getAppName, safeDivide } from 'src/utils/app';
import { useAppSelector } from 'src/utils/Hooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import EntityTableHeader, { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import ProductGrid, { ProductGridDatum } from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import _get from 'lodash/get';
import { shouldHidePagination } from 'src/components/BeaconV2Components/EntityTableRefresh/utils';
import { useAtlasV2TableData } from 'src/serverProxy/useAtlasV2TableData';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { Box } from '@mui/material';
import { useMainEntityId, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { getWeekColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { useDorisTableData } from 'src/serverProxy/useDorisTableData';

const PAGE_SIZE = 20;
const FETCH_CHUNK_SIZE = 100;

const PAGES_PER_CHUNK = Math.ceil(FETCH_CHUNK_SIZE / PAGE_SIZE);
const PRODUCT_API_METRIC_TYPE_BY_WEEK = 'gettrafficsummarybyweek';
const PRODUCT_API_METRIC_TYPE_BY_SKU = 'GetTrafficCountSummaryByType';

const METRICS = [
  ['purchaseRate', 'sales'],
  ['retailSales', 'sales'],
  ['unitsSold', 'sales'],
  ['retailPrice', 'sales']
];

const TableNoDataPlaceHolder = (
  <NoDataPlaceHolder
    title="Data currently unavailable"
    description="There is currently no data matching your selected criteria. Please try another query to view insights."
    flexProps={{
      paddingTop: '12.5px',
      paddingBottom: '12.5px'
    }}
  />
);

/**
 * Conversion rate grid in the traffic conversion page
 */
export default function AtlasV2ConversionV2ProductGrid() {
  const entityType = useMainEntityType();
  const isProductEntity = entityType === 'product';
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'tile' | 'table'>(isProductEntity ? 'table' : 'tile');
  const [dataByPageChunk, setDataByPageChunk] = useState<{ [key: number]: any[] }>({});

  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const mainEntityId = useMainEntityId();

  // Reset page when time periods change
  useEffect(() => {
    setPage(1);
    setDataByPageChunk({});
  }, [startWeek, endWeek, comparisonStartWeek, comparisonEndWeek]);

  const getKey = useCallback(
    (indexName: string, fieldName: string, comparison?: boolean) => {
      const field = INDEX_FIELDS.getField(getAppName(), indexName, fieldName);
      const fieldPrefix = `${fieldName}_${field.aggregationFunctionType || field.aggregationFunction}_value`;

      return comparison ? `${fieldPrefix}_${comparisonStartWeek}_${comparisonEndWeek}` : fieldPrefix;
    },
    [comparisonEndWeek, comparisonStartWeek]
  );

  /**
   * Returns the chunk number we are using to fetch. For example, if we show 20 items
   * per page and fetch 100 items, then page 1-5 will be chunk 1, page 6-10 will be chunk 2, etc.
   */
  const computeChunkPage = useCallback((displayedPage: number) => {
    return Math.ceil(displayedPage / PAGES_PER_CHUNK);
  }, []);

  const currentChunk = computeChunkPage(page);

  const { data: salesData, isLoading: salesLoading } = useAtlasV2TableData({
    indexName: 'sales',
    fields: [
      {
        name: 'retailSales'
      },
      {
        name: 'unitsSold'
      },
      {
        name: 'retailPrice'
      }
    ],
    groupByFieldName: isProductEntity ? 'weekId' : 'stacklineSku',
    itemsPerPage: FETCH_CHUNK_SIZE,
    pageNumber: currentChunk,
    requestId: 'traffic-conversion-rate-grid'
  });

  /**
   * Returned SKUs so that we can query traffic data for each SKU
   */
  const skus = isProductEntity
    ? []
    : salesData.map((row: { metadata: { product: ProductEntity } }) => row.metadata.product.stacklineSku);

  const { rawResponse, isLoading: dorisTrafficLoading } = useDorisTableData({
    apiMethodName: isProductEntity ? PRODUCT_API_METRIC_TYPE_BY_WEEK : PRODUCT_API_METRIC_TYPE_BY_SKU,
    groupByFieldName: 'product',
    metricType: 'totalClicks',
    additionalParams: {
      stacklineSkus: isProductEntity ? [mainEntityId] : skus
    },
    shouldPerformFetch: !salesLoading
  });

  /**
   * Combines sales and traffic data and computes conversion rate
   */
  const conversionRows = useMemo(() => {
    if (dorisTrafficLoading || salesLoading) {
      return [];
    }

    const trafficData = rawResponse.primaryData?.records || [];
    const trafficComparisonData = rawResponse.comparisonData?.records || [];

    if (isProductEntity) {
      const trafficByWeekId = trafficData.reduce((acc: any, row: any) => {
        const metric = row.totalClicks;
        return {
          ...acc,
          [row.weekId || row.metadata?.weekId]: {
            ...row,
            value: metric
          }
        };
      }, {});

      const rows = salesData.map((row: { weekId?: string; metadata: { weekId: string; name: string } }) => {
        const weekId = row.weekId || row.metadata.weekId;
        const trafficRow = trafficByWeekId[weekId];
        const totalClicks = trafficRow?.totalClicks;

        const weekData = {
          weekId,
          ...['retailSales', 'unitsSold', 'retailPrice'].reduce((acc, fieldName) => {
            const key = getKey('sales', fieldName);
            const metric = _get(row, key, 0);

            return {
              ...acc,
              [fieldName]: {
                value: metric
              }
            };
          }, {}),
          totalClicks: {
            value: totalClicks
          }
        };

        const unitsSold = _get(weekData, ['unitsSold', 'value'], 0);
        const purchaseRate = safeDivide(unitsSold, totalClicks);

        return {
          ...weekData,
          purchaseRate: {
            value: purchaseRate
          }
        };
      });
      return rows.sort((a, b) => parseInt(b.weekId, 10) - parseInt(a.weekId, 10));
    }

    const stacklineSkuToTrafficData = trafficData.reduce((acc: any, row: any) => {
      const sku = row.metadata?.stacklineSku || row.stacklineSku;
      if (sku) {
        acc[sku] = row;
      }
      return acc;
    }, {});

    const stacklineSkuToTrafficComparisonData = trafficComparisonData.reduce((acc: any, row: any) => {
      const sku = row.metadata?.stacklineSku || row.stacklineSku;
      if (sku) {
        acc[sku] = row;
      }
      return acc;
    }, {});

    const rows = salesData.map((row: { metadata: { product: ProductEntity } }) => {
      const trafficRow = stacklineSkuToTrafficData[row.metadata.product.stacklineSku];
      const totalClicks = trafficRow?.totalClicks;
      const totalClicksComparison = stacklineSkuToTrafficComparisonData[row.metadata.product.stacklineSku]?.totalClicks;

      const productGridDatum = {
        product: row.metadata.product,
        ...['retailSales', 'unitsSold', 'retailPrice'].reduce((acc, fieldName) => {
          const key = getKey('sales', fieldName);
          const comparisonKey = getKey('sales', fieldName, true);

          const metric = _get(row, key, 0);
          const comparison = _get(row, comparisonKey, 0);

          return {
            ...acc,
            [fieldName]: {
              value: metric,
              difference: metric - comparison
            }
          };
        }, {}),
        totalClicks: {
          value: totalClicks,
          difference: totalClicks - totalClicksComparison
        }
      };

      const unitsSold = _get(productGridDatum, ['unitsSold', 'value'], 0);
      const unitsSoldComparison = unitsSold - _get(productGridDatum, ['unitsSold', 'difference'], 0);

      const purchaseRate = safeDivide(unitsSold, totalClicks);
      const purchaseRateComparison = safeDivide(unitsSoldComparison, totalClicksComparison);

      return {
        ...productGridDatum,
        purchaseRate: {
          value: purchaseRate,
          difference: purchaseRate - purchaseRateComparison
        }
      };
    });

    return rows;
  }, [
    dorisTrafficLoading,
    getKey,
    isProductEntity,
    rawResponse.comparisonData?.records,
    rawResponse.primaryData?.records,
    salesData,
    salesLoading
  ]);

  /**
   * Updates the cached data when new data is fetched
   */
  useEffect(() => {
    if (conversionRows.length > 0) {
      setDataByPageChunk((oldData) => ({
        ...oldData,
        [currentChunk]: conversionRows
      }));
    }
  }, [conversionRows, currentChunk]);

  /**
   * The full table data from all cached chunks
   */
  const fullTableData = useMemo(() => {
    return Object.keys(dataByPageChunk)
      .sort((a, b) => Number(a) - Number(b))
      .reduce((acc: any[], chunkNumber) => {
        return [...acc, ...dataByPageChunk[Number(chunkNumber)]];
      }, []);
  }, [dataByPageChunk]);

  const startIndex = (page - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const currentPageData = fullTableData.slice(startIndex, endIndex);

  const handleNextPage = useCallback(() => {
    const adjustedPage = ((page - 1) % PAGES_PER_CHUNK) + 1;
    const startIndex = (adjustedPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;

    const currentChunkData = dataByPageChunk[currentChunk] || [];

    // If we don't have enough data in current chunk, don't proceed
    if (currentChunkData.slice(startIndex, endIndex).length < PAGE_SIZE && currentChunkData.length < FETCH_CHUNK_SIZE) {
      return;
    }

    setPage(page + 1);
  }, [page, dataByPageChunk, currentChunk]);

  const handlePreviousPage = useCallback(() => {
    setPage(Math.max(page - 1, 1));
  }, [page]);

  return (
    <Flex flexDirection="column" marginTop="104px">
      <EntityTableHeader
        title={`Conversion Rate by ${isProductEntity ? 'Week' : 'Product'}`}
        enableSwitchingLayouts
        defaultView={viewMode}
        handleChangeLayout={() => setViewMode(viewMode === 'table' ? 'tile' : 'table')}
        paddingTop="0px"
        iconsList={isProductEntity ? [IconsList.export, IconsList.table] : undefined}
      />
      <Flex flexDirection="column" gap={viewMode === 'table' ? 0 : 20}>
        {viewMode === 'table' ? (
          isProductEntity ? (
            <EntityTable
              skeletonRows={PAGE_SIZE}
              isLoading={salesLoading || dorisTrafficLoading}
              showPagination={false}
              shouldModifyColumnDefs={false}
              columnDefs={[
                getWeekColumnDefinition({
                  getTableColumnProps(defaults) {
                    return {
                      ...defaults,
                      style: {
                        ...defaults.style,
                        width: '235px !important'
                      }
                    };
                  }
                }),
                ...METRICS.map(([fieldName, indexName]) => {
                  const field = INDEX_FIELDS.getField(getAppName(), indexName, fieldName);

                  return {
                    headerName: field.displayName,
                    valueFormatter: (_, row: ProductGridDatum) => {
                      const metricValue = _get(row, [fieldName, 'value'], 0);
                      return <MetricCell metricType={field.metricType} value={metricValue} />;
                    }
                  };
                })
              ]}
              rowData={currentPageData}
            />
          ) : (
            <EntityTable
              skeletonRows={PAGE_SIZE}
              isLoading={salesLoading || dorisTrafficLoading}
              showPagination={false}
              shouldModifyColumnDefs={false}
              columnDefs={[
                {
                  headerName: 'Products',
                  valueFormatter: (_, row: ProductGridDatum) => (
                    <div style={{ width: '359px' }}>
                      <ProductCell
                        key={row.product.stacklineSku}
                        stacklineSku={row.product.stacklineSku}
                        title={row.product.title}
                      />
                    </div>
                  )
                },
                ...METRICS.map(([fieldName, indexName]) => {
                  const field = INDEX_FIELDS.getField(getAppName(), indexName, fieldName);
                  return {
                    headerName: field.displayName,
                    valueFormatter: (_, row: ProductGridDatum) => {
                      const metricValue = _get(row, [fieldName, 'value'], 0);
                      const comparisonValue = metricValue - _get(row, [fieldName, 'difference'], 0);
                      const percentChange = calculatePercentChange(metricValue, comparisonValue);

                      return (
                        <MetricCell
                          metricType={field.metricType}
                          value={metricValue}
                          percentChange={fieldName === 'totalClicks' ? undefined : percentChange}
                        />
                      );
                    }
                  };
                })
              ]}
              rowData={currentPageData}
              appTableProps={{
                noDataPlaceholder: TableNoDataPlaceHolder,
                sx: {
                  'tr td:first-child': {
                    width: '424px',
                    paddingRight: '40px'
                  }
                }
              }}
            />
          )
        ) : (
          <ProductGrid
            data={currentPageData as unknown as ProductGridDatum[]}
            loading={salesLoading || dorisTrafficLoading}
            metricType={METRICTYPE.PERCENT}
            fieldName="purchaseRate"
            page={1}
            noDataPlaceholder={
              <Box sx={{ display: 'flex', justifyContent: 'center', margin: '80px 0' }}>
                <NoDataPlaceHolder
                  title="Data currently unavailable"
                  description="There is currently no data matching your selected criteria. Please try another query to view insights."
                  flexProps={{
                    padding: 0
                  }}
                />
              </Box>
            }
          />
        )}
        {shouldHidePagination({ page, pageSize: PAGE_SIZE, rowCount: fullTableData.length }) ? null : (
          <Flex justifyContent="flex-end">
            <Pagination
              currentPage={page}
              shouldUseInfinitePagination
              onClickNext={handleNextPage}
              onClickPrevious={handlePreviousPage}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
